import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Loader2, Settings, LogOut, ArrowLeft, Save, ExternalLink, Mail, MailX, CreditCard } from 'lucide-react';
import { NewsletterTextarea } from '@/components/NewsletterTextarea';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { TrialStatusCard } from '@/components/TrialStatusCard';
import { Switch } from '@/components/ui/switch';
import { Link } from 'react-router-dom';
import { trackEvent, trackConversion } from '@/components/GoogleAnalytics';
import { trackEventMp, trackConversionMp } from '@/components/Mixpanel';
import { trackEvent as trackEventHj } from '@/components/Hotjar';
import { useUserAwarePricing } from '@/hooks/useUserAwarePricing';
import { PaymentTokenHandler } from '@/components/PaymentTokenHandler';
import { WelcomeSuccessModal } from '@/components/funnel/WelcomeSuccessModal';
import { useRef } from 'react';
import { PremiumSettingsCard } from '@/components/preferences/PremiumSettingsCard';

interface UserData {
  id: string;
  email: string;
  interests: string;
  newsletter_theme?: 'light_professional' | 'dark_legacy';
  module_daily_reflection?: boolean;
  module_daily_curiosity?: boolean;
  module_business_tip?: boolean;
  module_github_trending?: boolean;
}

interface SessionData {
  session_token: string;
  expires_at: string;
}

const Preferences = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [userData, setUserData] = useState<UserData | null>(null);
  const [sessionData, setSessionData] = useState<SessionData | null>(null);
  const [interests, setInterests] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [isPortalLoading, setIsPortalLoading] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState<any>(null);
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(true);
  const [emailNotificationsEnabled, setEmailNotificationsEnabled] = useState<boolean>(true);
  const [isTogglingNotifications, setIsTogglingNotifications] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [confettiTriggered, setConfettiTriggered] = useState(false);
  const interestsRef = useRef<HTMLTextAreaElement>(null);
  const [premiumSettings, setPremiumSettings] = useState({
    theme: 'light_professional' as 'light_professional' | 'dark_legacy',
    modules: {
      dailyReflection: true,
      dailyCuriosity: true,
      businessTip: true,
      githubTrending: false
    },
    timezone: '', // Start empty - will be populated after fetching from DB to prevent race conditions
    preferredDeliveryHour: 7
  });
  const [isPremiumDataLoaded, setIsPremiumDataLoaded] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    const token = searchParams.get('token');
    const checkout = searchParams.get('checkout');
    const autoLogin = searchParams.get('auto_login');
    const paymentToken = searchParams.get('payment_token');
    const flow = searchParams.get('flow');
    const sessionId = searchParams.get('session_id');
    const storedSession = localStorage.getItem('newsletter_session');

    // Handle inline checkout success (new flow)
    if (checkout === 'success' && flow === 'inline' && sessionId) {
      console.info('[INLINE-CHECKOUT] Starting Stripe session exchange');
      handleInlineCheckoutSuccess(sessionId);
      return;
    }

    // Handle payment token exchange for checkout success with auto login
    if (checkout === 'success' && paymentToken && autoLogin) {
      console.info('[PAYMENT-FLOW] Starting payment token exchange for checkout success');
      handlePaymentTokenExchange(paymentToken);
      return;
    }

    if (token) {
      validateMagicToken(token);
    } else if (paymentToken && !storedSession) {
      // Regular payment token flow (not from checkout success)
      setIsLoading(true);
    } else if (storedSession) {
      try {
        const sessionData = JSON.parse(storedSession);
        validateSession(sessionData.session_token);
        
      // Handle checkout success notification and process pending campaigns
      if (checkout === 'success') {
        handlePaymentSuccessNotification(sessionData.session_token, null);
        handleCheckoutSuccess(sessionData.session_token);
      }
      } catch (parseError) {
        // Handle old format (plain token string) - fallback compatibility
        console.log('Legacy token format detected, treating as plain string');
        validateSession(storedSession);
        
        // Handle checkout success notification for legacy format
        if (checkout === 'success') {
          handlePaymentSuccessNotification(storedSession, null);
          handleCheckoutSuccess(storedSession);
        }
      }
    } else {
      // Auto-redirect to preferences request page instead of showing error
      navigate('/preferences/request');
      return;
    }
  }, [searchParams]);

  const handleInlineCheckoutSuccess = async (sessionId: string, retries = 3, delay = 2000) => {
    for (let attempt = 1; attempt <= retries; attempt++) {
      try {
        console.log(`[INLINE-CHECKOUT] Exchanging Stripe session (attempt ${attempt}/${retries})`);
        
        const { data, error } = await supabase.functions.invoke('exchange-stripe-session', {
          body: { session_id: sessionId }
        });

        if (error) {
          // If it's "Subscription not found" and we have retries left, wait and retry
          if (error.message?.includes('Subscription not found') && attempt < retries) {
            console.log(`[INLINE-CHECKOUT] Subscription not found, retrying in ${delay}ms... (${attempt}/${retries})`);
            await new Promise(resolve => setTimeout(resolve, delay));
            continue;
          }
          
          // Otherwise, throw the error
          throw error;
        }

        if (data?.session_token && data?.user) {
          console.log('[INLINE-CHECKOUT] Session exchanged successfully');
          
          // Store session
          localStorage.setItem('newsletter_session', JSON.stringify({
            user_id: data.user.id,
            email: data.user.email,
            session_token: data.session_token,
            expires_at: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
          }));

          // Clean funnel-related data after successful conversion
          localStorage.removeItem('comeback_prospect');
          localStorage.removeItem('funnel_session');
          console.log('[INLINE-CHECKOUT] Cleaned funnel data after successful conversion');

          // Set user data
          setUserData(data.user);
          setInterests(data.user.interests || '');
          setSessionData({
            session_token: data.session_token,
            expires_at: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
          });

          // Fetch email notifications
          fetchEmailNotificationStatus(data.session_token, data.user.id);

          // Fetch subscription status to update premium card
          try {
            const { data: subData, error: subError } = await supabase.functions.invoke('check-subscription', {
              body: { session_token: data.session_token }
            });

            if (!subError && subData) {
              setSubscriptionStatus(subData);
            }
          } catch (err) {
            console.error('[INLINE-CHECKOUT] Failed to fetch subscription status:', err);
          }

          // 🆕 FETCH COMPLETE USER DATA INCLUDING MODULES
          try {
            const { data: prefsData, error: prefsError } = await supabase.functions.invoke('get-preferences', {
              body: { session_token: data.session_token }
            });

            if (!prefsError && prefsData?.success && prefsData?.user) {
              console.log('[INLINE-CHECKOUT] Loaded preferences from backend:', prefsData.user);
              
              // Update interests with fresh data
              setInterests(prefsData.user.interests || '');
              
              // 🔑 CRITICAL: Populate premium settings with actual values from backend
              setPremiumSettings({
                theme: prefsData.user.newsletter_theme || 'light_professional',
                modules: {
                  dailyReflection: prefsData.user.module_daily_reflection ?? true,
                  dailyCuriosity: prefsData.user.module_daily_curiosity ?? true,
                  businessTip: prefsData.user.module_business_tip ?? true,
                  githubTrending: prefsData.user.module_github_trending ?? false
                },
                timezone: prefsData.user.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
                preferredDeliveryHour: prefsData.user.preferred_delivery_hour || 7
              });
              setIsPremiumDataLoaded(true);
              
              console.log('[INLINE-CHECKOUT] Premium settings populated:', {
                theme: prefsData.user.newsletter_theme,
                modules: {
                  dailyReflection: prefsData.user.module_daily_reflection,
                  dailyCuriosity: prefsData.user.module_daily_curiosity,
                  businessTip: prefsData.user.module_business_tip,
                  githubTrending: prefsData.user.module_github_trending
                }
              });
            }
          } catch (prefsErr) {
            console.error('[INLINE-CHECKOUT] Failed to fetch preferences:', prefsErr);
            // Don't fail the whole flow, just log the error
          }

          // Close "Validating access" modal before confetti
          setIsLoading(false);
          setIsLoadingSubscription(false);

          // Track success
          trackEventMp('inline_checkout_success_landed', {
            email: data.user.email,
            session_id: sessionId
          });

          // Trigger confetti and modal after a brief delay
          setTimeout(async () => {
            try {
              const confettiModule = await import('@/utils/confetti');
              confettiModule.triggerSuccessConfetti();
              setConfettiTriggered(true);
              
              trackEventMp('confetti_triggered', {
                email: data.user.email,
                trigger_time: Date.now()
              });
            } catch (err) {
              console.error('[INLINE-CHECKOUT] Confetti load failed:', err);
            }
            
            setTimeout(() => {
              setShowWelcomeModal(true);
            }, 800);
          }, 300);

          // Clean URL
          window.history.replaceState({}, document.title, '/preferences');
          
          // Exit retry loop on success
          return;
        }
      } catch (error: any) {
        console.error(`[INLINE-CHECKOUT] Error on attempt ${attempt}:`, error);
        
        // If last attempt, show error and redirect
        if (attempt === retries) {
          toast({
            title: "Erro na autenticação",
            description: "Redirecionando para inscrição...",
            variant: "destructive"
          });
          setTimeout(() => navigate('/subscribe'), 2000);
        }
      }
    }
    
    setIsLoading(false);
  };

  const validateMagicToken = async (token: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('validate-magic-link', {
        body: { token }
      });

      if (error) throw error;

      if (data?.error) {
        // Redirect to request page for invalid magic token
        navigate('/preferences/request');
        return;
      }

      if (data?.success && data?.user && data?.session) {
        setUserData(data.user);
        setInterests(data.user.interests);
        setSessionData(data.session);
        
        localStorage.setItem('newsletter_session', JSON.stringify({
          user_id: data.user.id,
          email: data.user.email,
          session_token: data.session.token,
          expires_at: data.session.expires_at
        }));

        // Fetch subscription status after magic link validation
        try {
          const { data: subData } = await supabase.functions.invoke('check-subscription', {
            body: { session_token: data.session.token }
          });
          
          if (subData) {
            setSubscriptionStatus(subData);
          }
        } catch (err) {
          console.error('[MAGIC-LINK] Failed to fetch subscription:', err);
        }

        // Fetch email notifications status
        fetchEmailNotificationStatus(data.session.token, data.user.id);

        // Fetch complete preferences (timezone, modules, theme)
        try {
          const { data: prefsData, error: prefsError } = await supabase.functions.invoke('get-preferences', {
            body: { session_token: data.session.token }
          });

          if (!prefsError && prefsData?.success && prefsData?.user) {
            console.log('[MAGIC-LINK] Loaded complete preferences:', {
              timezone: prefsData.user.timezone,
              theme: prefsData.user.newsletter_theme,
              modules: {
                dailyReflection: prefsData.user.module_daily_reflection,
                dailyCuriosity: prefsData.user.module_daily_curiosity,
                businessTip: prefsData.user.module_business_tip,
                githubTrending: prefsData.user.module_github_trending
              }
            });
            
            setPremiumSettings({
              theme: prefsData.user.newsletter_theme || 'light_professional',
              modules: {
                dailyReflection: prefsData.user.module_daily_reflection ?? true,
                dailyCuriosity: prefsData.user.module_daily_curiosity ?? true,
                businessTip: prefsData.user.module_business_tip ?? true,
                githubTrending: prefsData.user.module_github_trending ?? false
              },
              timezone: prefsData.user.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
              preferredDeliveryHour: prefsData.user.preferred_delivery_hour || 7
            });
            setIsPremiumDataLoaded(true);
          }
        } catch (prefsErr) {
          console.error('[MAGIC-LINK] Failed to fetch preferences:', prefsErr);
          setIsPremiumDataLoaded(true);
        }

        window.history.replaceState({}, document.title, '/preferences');
      }
    } catch (error: any) {
      console.error('Error validating magic token:', error);
      setError(t('preferences.errors.validationError'));
    } finally {
      setIsLoading(false);
      setIsLoadingSubscription(false);
    }
  };

  const handleCheckoutSuccess = async (sessionToken: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('check-subscription', {
        body: { session_token: sessionToken }
      });

      if (error) {
        return;
      }

      // Promotion bonus processing is now handled automatically in check-subscription
    } catch (error: any) {
      // Silent error handling in production
    }
  };

  const handlePaymentSuccessNotification = async (sessionToken: string | null, paymentToken: string | null) => {
    try {
      const requestBody: any = {};
      if (sessionToken) requestBody.session_token = sessionToken;
      if (paymentToken) requestBody.payment_token = paymentToken;
      
      const { data, error } = await supabase.functions.invoke('payment-success-notification', {
        body: requestBody
      });

      if (error) {
        return;
      }
      
      if (data?.notification_sent) {
        toast({
          title: t('preferences.toasts.success'),
          description: 'Pagamento processado com sucesso!',
        });
      }
    } catch (error: any) {
      // Silent error handling in production
    }
  };

  const validateSession = async (sessionToken: string) => {
    try {
      console.log('[PREFERENCES] Validating session token...');
      
      if (!sessionToken || sessionToken.trim() === '') {
        console.error('[PREFERENCES] Invalid session token - empty or null');
        // Try to get email before clearing session for redirect
        const storedSession = localStorage.getItem('newsletter_session');
        let emailToRedirect = '';
        if (storedSession) {
          try {
            const sessionData = JSON.parse(storedSession);
            emailToRedirect = sessionData.email || '';
          } catch (e) {}
        }
        localStorage.removeItem('newsletter_session');
        // Redirect to request page with email pre-filled
        const redirectUrl = emailToRedirect 
          ? `/preferences/request?email=${encodeURIComponent(emailToRedirect)}`
          : '/preferences/request';
        navigate(redirectUrl);
        return;
      }

      const { data, error } = await supabase.functions.invoke('get-preferences', {
        body: { session_token: sessionToken }
      });

      if (error) {
        console.error('[PREFERENCES] Supabase function error:', error);
        throw error;
      }

      if (data?.error) {
        console.log('[PREFERENCES] Session validation failed:', data.error);
        // Try to get email before clearing session for redirect
        const storedSession = localStorage.getItem('newsletter_session');
        let emailToRedirect = '';
        if (storedSession) {
          try {
            const sessionData = JSON.parse(storedSession);
            emailToRedirect = sessionData.email || '';
          } catch (e) {}
        }
        localStorage.removeItem('newsletter_session');
        // Redirect to request page with email pre-filled
        const redirectUrl = emailToRedirect 
          ? `/preferences/request?email=${encodeURIComponent(emailToRedirect)}`
          : '/preferences/request';
        navigate(redirectUrl);
        return;
      }

      if (data?.success && data?.user) {
        console.log('[PREFERENCES] Session validated successfully for user:', data.user.email);
        setUserData(data.user);
        setInterests(data.user.interests || '');
        
        // Load premium settings
        setPremiumSettings({
          theme: data.user.newsletter_theme || 'light_professional',
          modules: {
            dailyReflection: data.user.module_daily_reflection ?? true,
            dailyCuriosity: data.user.module_daily_curiosity ?? true,
            businessTip: data.user.module_business_tip ?? true,
            githubTrending: data.user.module_github_trending ?? false
          },
          timezone: data.user.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
          preferredDeliveryHour: data.user.preferred_delivery_hour || 7
        });
        setIsPremiumDataLoaded(true);
        
        // Set subscription status
        if (data.subscription) {
          setSubscriptionStatus(data.subscription);
        }
        
        try {
          const storedSession = JSON.parse(localStorage.getItem('newsletter_session') || '{}');
          setSessionData({ 
            session_token: storedSession.session_token || sessionToken, 
            expires_at: data.session?.expires_at || storedSession.expires_at
          });
          
          // Fetch email notifications status
          await fetchEmailNotificationStatus(storedSession.session_token || sessionToken, data.user.id);
        } catch (parseError) {
          console.error('[PREFERENCES] Error parsing stored session:', parseError);
          // Create minimal session data
          setSessionData({ 
            session_token: sessionToken, 
            expires_at: data.session?.expires_at || new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString()
          });
        }
        
        setError(''); // Clear any previous errors
      } else {
        console.error('[PREFERENCES] Unexpected response format:', data);
        localStorage.removeItem('newsletter_session');
        setError(t('preferences.errors.sessionValidationError'));
      }
    } catch (error: any) {
      console.error('[PREFERENCES] Error validating session:', error);
      localStorage.removeItem('newsletter_session');
      setError(error.message || t('preferences.errors.sessionValidationError'));
      } finally {
        setIsLoading(false);
        setIsLoadingSubscription(false);
      }
  };

  const handleSavePreferences = async () => {
    if (!interests || interests.length < 70 || interests.length > 500) {
      toast({
        title: t('preferences.errors.errorTitle'),
        description: t('preferences.errors.interestsLength'),
        variant: 'destructive'
      });
      return;
    }

    setIsSaving(true);

    try {
      const storedSession = localStorage.getItem('newsletter_session');
      const sessionData = storedSession ? JSON.parse(storedSession) : null;

      // Track analytics event
      const eventParams = {
        app_version: '3.0',
        layout_version: 'interactive_preview',
        event_category: 'user_action',
        interests_length: interests.length
      };
      
      trackEvent('preferences_saved', eventParams);
      trackEventMp('preferences_saved', eventParams);
      trackEventHj('preferences_saved');

      const { data, error } = await supabase.functions.invoke('update-preferences', {
        body: { 
          session_token: sessionData?.session_token,
          interests: interests.trim()
        }
      });

      if (error) throw error;

      if (data?.error) {
        toast({
          title: t('preferences.errors.errorTitle'),
          description: data.error,
          variant: 'destructive'
        });
        return;
      }

      if (data?.success) {
        toast({
          title: t('preferences.toasts.success'),
          description: t('preferences.toasts.preferencesUpdated')
        });
        
        if (data.user) {
          setUserData(data.user);
        }
      }
    } catch (error: any) {
      console.error('Error updating preferences:', error);
      toast({
        title: t('preferences.errors.errorTitle'),
        description: t('preferences.errors.updateError'),
        variant: 'destructive'
      });
    } finally {
      setIsSaving(false);
    }
  };
  

  const handleManageSubscription = async () => {
    try {
      setIsPortalLoading(true);
      
      // Track analytics event
      const eventParams = {
        app_version: '3.0',
        layout_version: 'interactive_preview',
        event_category: 'user_action'
      };
      
      trackEvent('customer_portal_opened', eventParams);
      trackEventMp('customer_portal_opened', eventParams);
      trackEventHj('customer_portal_opened');
      
      const storedSession = localStorage.getItem('newsletter_session');
      const sessionData = storedSession ? JSON.parse(storedSession) : null;
      if (!sessionData?.session_token) {
        toast({ 
          title: t('preferences.toasts.sessionInvalid'),
          description: t('preferences.toasts.sessionInvalidManage'),
          variant: 'destructive' 
        });
        return;
      }
      
      const { data, error } = await supabase.functions.invoke('customer-portal', {
        body: { session_token: sessionData.session_token }
      });
      
      if (error) {
        toast({ 
          title: t('preferences.toasts.communicationError'),
          description: `${t('preferences.toasts.communicationErrorDesc')} ${error.message}`,
          variant: 'destructive' 
        });
        return;
      }
      
      if (data?.error || !data?.url) {
        toast({ 
          title: t('preferences.toasts.portalError'),
          description: data?.error || t('preferences.toasts.portalErrorDesc'),
          variant: 'destructive' 
        });
        return;
      }
      toast({
        title: t('preferences.toasts.redirecting'),
        description: t('preferences.toasts.portalRedirect'),
      });
      
      window.location.href = data.url;
    } catch (err) {
      toast({ 
        title: t('preferences.toasts.unexpectedError'),
        description: t('preferences.toasts.portalUnexpectedDesc'),
        variant: 'destructive' 
      });
    } finally {
      setIsPortalLoading(false);
    }
  };

  const fetchEmailNotificationStatus = async (sessionToken: string, userId: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('get-subscription-info', {
        body: { subscription_id: userId, session_token: sessionToken }
      });

      if (error) {
        console.error('Error fetching email notification status:', error);
        return;
      }

      if (data?.email_notifications_enabled !== undefined) {
        setEmailNotificationsEnabled(data.email_notifications_enabled);
      }
    } catch (error) {
      console.error('Error fetching email notification status:', error);
    }
  };

  const handleToggleEmailNotifications = async () => {
    if (!userData?.id || !sessionData?.session_token) {
      toast({
        title: t('preferences.errors.errorTitle'),
        description: t('preferences.toasts.sessionInvalid'),
        variant: 'destructive'
      });
      return;
    }

    setIsTogglingNotifications(true);

    try {
      const action = emailNotificationsEnabled ? 'pause' : 'resubscribe';
      
      // Track analytics event
      const eventParams = {
        app_version: '3.0',
        layout_version: 'interactive_preview',
        event_category: 'user_action',
        action: action,
        email: userData.email?.split('@')[0] + '@***'
      };
      
      trackEvent('email_notifications_toggled', eventParams);
      trackEventMp('email_notifications_toggled', eventParams);
      trackEventHj('email_notifications_toggled');
      
      const { data, error } = await supabase.functions.invoke('unsubscribe', {
        body: { 
          subscriptionId: userData.id,
          session_token: sessionData.session_token,
          action: action
        }
      });

      if (error) throw error;

      if (data?.error) {
        toast({
          title: t('preferences.errors.errorTitle'),
          description: data.error,
          variant: 'destructive'
        });
        return;
      }

      if (data?.success) {
        setEmailNotificationsEnabled(!emailNotificationsEnabled);
        toast({
          title: t('preferences.toasts.success'),
          description: emailNotificationsEnabled 
            ? t('preferences.toasts.emailsDisabled')
            : t('preferences.toasts.emailsEnabled')
        });
      }
    } catch (error: any) {
      console.error('Error toggling email notifications:', error);
      toast({
        title: t('preferences.errors.errorTitle'),
        description: t('preferences.errors.toggleError'),
        variant: 'destructive'
      });
    } finally {
      setIsTogglingNotifications(false);
    }
  };

  const handleLogout = () => {
    // Track analytics event
    const eventParams = {
      app_version: '3.0',
      layout_version: 'interactive_preview',
      event_category: 'user_action'
    };
    
    trackEvent('user_logged_out', eventParams);
    trackEventMp('user_logged_out', eventParams);
    trackEventHj('user_logged_out');
    
    localStorage.removeItem('newsletter_session');
    navigate('/');
  };

  const handlePaymentTokenExchange = async (paymentToken: string) => {
    setIsLoading(true);
    setError(null);

    let attempts = 0;
    const maxAttempts = 3;
    const delays = [0, 1000, 2000]; // 0ms, 1s, 2s

    // Track last function error details to decide behavior after retries
    let lastErrorStatus: number | undefined;
    let lastErrorCode: string | undefined;

    while (attempts < maxAttempts) {
      attempts++;
      
      try {
        if (attempts > 1) {
          console.info(`[PAYMENT-FLOW] Retry attempt ${attempts}/${maxAttempts}`);
          await new Promise(resolve => setTimeout(resolve, delays[attempts - 1]));
        }

        const { data: response, error: functionError } = await supabase.functions.invoke('exchange-payment-token', {
          body: { payment_token: paymentToken }
        });

        if (functionError) {
          // Parse error body to handle specific cases before throwing
          let errorBody: any = {};
          try {
            const ctxBody = (functionError as any)?.context?.body;
            if (ctxBody && typeof ctxBody === 'string') {
              errorBody = JSON.parse(ctxBody);
            } else if ((functionError as any)?.context?.error) {
              errorBody = (functionError as any).context.error;
              if (typeof errorBody === 'string') {
                try { errorBody = JSON.parse(errorBody); } catch {}
              }
            }
          } catch {}

          const status = (functionError as any)?.context?.status as number | undefined;
          const code = (errorBody?.error || errorBody?.code || errorBody?.message) as string | undefined;
          lastErrorStatus = status;
          if (typeof code === 'string') lastErrorCode = code;
          
          console.info('[PAYMENT-FLOW] Function error details:', {
            status,
            body: errorBody,
            attempt: attempts
          });

          // Handle specific error cases and 401 fallback
          if (code === 'PAYMENT_LINK_ALREADY_USED' || code === 'INVALID_PAYMENT_TOKEN' || status === 401) {
            console.info('[PAYMENT-FLOW] Token invalid/used or 401. Redirecting to preferences');
            window.location.href = '/preferences';
            return;
          }

          // Handle payment processing - retry if we have attempts left
          if ((code === 'PAYMENT_PROCESSING' || status === 409) && attempts < maxAttempts) {
            console.info('[PAYMENT-FLOW] Payment still processing, retrying...');
            continue;
          }

          throw functionError;
        }

        if (response?.error) {
          // Handle payment link already used - redirect to clean preferences
          if (response.error === 'PAYMENT_LINK_ALREADY_USED') {
            console.info('[PAYMENT-FLOW] Payment link already used, redirecting to preferences');
            // Force full page reload to /preferences
            window.location.href = '/preferences';
            return;
          }
          
          if (response.error === 'Pagamento ainda sendo processado' && attempts < maxAttempts) {
            console.info('[PAYMENT-FLOW] Payment processing, retrying...');
            continue;
          }
          throw new Error(response.error);
        }

        if (response?.success) {
          console.info('[PAYMENT-FLOW] Payment token exchanged successfully');
          
          // Save real session token
          localStorage.setItem('newsletter_session', JSON.stringify({
            session_token: response.session.token,
            expires_at: response.session.expires_at
          }));
          
          // Update state with user data
          setUserData(response.user);
          setInterests(response.user.interests || '');
          setPaymentSuccess(true);
          
          // Fetch subscription status
          try {
            const { data: subData } = await supabase.functions.invoke('check-subscription', {
              body: { session_token: response.session.token }
            });
            
            if (subData) {
              setSubscriptionStatus(subData);
            }
          } catch (err) {
            console.error('[PAYMENT-FLOW] Failed to fetch subscription:', err);
          }
          
          // Show success message
          toast({
            title: "🎉 Pagamento realizado com sucesso!",
            description: "Bem-vindo! Sua assinatura está ativa.",
          });

          // Clean URL after successful authentication
          console.info('[PAYMENT-FLOW] Cleaning URL after successful authentication');
          window.history.replaceState({}, document.title, '/preferences');

          setIsLoading(false);
          setIsLoadingSubscription(false);
          return;
        }
      } catch (err: any) {
        console.error(`[PAYMENT-FLOW] Attempt ${attempts} failed:`, err);
        
        if (attempts === maxAttempts) {
          if (lastErrorStatus === 401 || (lastErrorCode && (lastErrorCode === 'PAYMENT_LINK_ALREADY_USED' || lastErrorCode === 'INVALID_PAYMENT_TOKEN'))) {
            console.info('[PAYMENT-FLOW] Final attempt failed with 401/used token. Redirecting silently to /preferences');
            window.location.href = '/preferences';
            return;
          }
          setError('Erro ao processar pagamento. Tente solicitar um novo link de acesso.');
          toast({
            title: "Erro no processamento",
            description: "Houve um problema ao confirmar seu pagamento. Tente novamente.",
            variant: "destructive",
          });
        }
      }
    }
    
    setIsLoading(false);
  };

  const handlePaymentTokenData = (data: any) => {
    if (data && data.subscription_status === 'active') {
      setPaymentSuccess(true);
      setUserData({
        id: data.id,
        email: data.email,
        interests: data.interests || ''
      });
      setInterests(data.interests || '');
      setSubscriptionStatus(data);
      
      // Create session data from payment validation
      const newSessionData = {
        session_token: 'payment-session-' + Date.now(),
        expires_at: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString()
      };
      setSessionData(newSessionData);
      
      localStorage.setItem('newsletter_session', JSON.stringify({
        user_id: data.id,
        email: data.email,
        session_token: newSessionData.session_token,
        expires_at: newSessionData.expires_at
      }));
      
      setEmailNotificationsEnabled(data.email_notifications_enabled ?? true);
    }
  };

  const handlePaymentTokenLoading = (loading: boolean) => {
    setIsLoading(loading);
  };

  const handleWelcomeModalCustomize = () => {
    // Scroll to interests section and focus textarea
    const interestsSection = document.getElementById('interests-section');
    if (interestsSection) {
      interestsSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => {
        interestsRef.current?.focus();
      }, 500);
    }
  };

  if (isLoading) {
    const checkout = searchParams.get('checkout');
    const paymentToken = searchParams.get('payment_token');
    const loadingMessage = checkout === 'success' && paymentToken 
      ? t('preferences.loading.processingPayment')
      : t('preferences.loading.validatingAccess');
      
    return (
      <div className="min-h-screen bg-background relative overflow-hidden flex items-center justify-center">
        <div className="absolute inset-0 bg-newsletter-gradient opacity-5" />
        <Card className="w-full max-w-2xl mx-4">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin mr-3 text-newsletter-primary mb-4" />
            <span className="text-lg mb-2">{loadingMessage}</span>
            {checkout === 'success' && paymentToken && (
              <p className="text-sm text-muted-foreground">{t('preferences.loading.confirmingSubscription')}</p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !userData) {
    const paymentToken = searchParams.get('payment_token');
    const checkout = searchParams.get('checkout');
    const isCheckoutSuccess = checkout === 'success';
    
    return (
      <div className="min-h-screen bg-background relative overflow-hidden flex items-center justify-center">
        <div className="absolute inset-0 bg-newsletter-gradient opacity-5 pointer-events-none" />
        {paymentToken && !isCheckoutSuccess && (
          <PaymentTokenHandler
            paymentToken={paymentToken}
            onDataLoaded={handlePaymentTokenData}
            onLoading={handlePaymentTokenLoading}
          />
        )}
        <Card className="w-full max-w-md mx-4 relative z-10">
          <CardHeader className="text-center">
            {paymentSuccess ? (
              <>
                <div className="text-green-600 text-5xl mb-4">🎉</div>
                <CardTitle className="text-xl text-green-600">{t('preferences.payment.success')}</CardTitle>
                <CardDescription>
                  {isCheckoutSuccess && !paymentSuccess 
                    ? "Seu pagamento foi processado com sucesso. Para acessar suas preferências, solicite um novo link de acesso."
                    : t('preferences.payment.successDesc')
                  }
                </CardDescription>
              </>
            ) : (
              <>
                <div className="text-red-600 text-5xl mb-4">❌</div>
                <CardTitle className="text-xl text-destructive">{t('preferences.loading.accessDenied')}</CardTitle>
                <CardDescription>
                  {error || t('preferences.errors.accessDeniedDesc')}
                </CardDescription>
              </>
            )}
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            {paymentSuccess ? (
              <>
                <Button 
                  onClick={() => window.location.reload()}
                  className="w-full bg-green-600 hover:bg-green-700 text-white"
                >
                  {t('preferences.payment.accessSettings')}
                </Button>
                <Link to="/">
                  <Button variant="ghost" className="w-full">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    {t('preferences.loading.backToHome')}
                  </Button>
                </Link>
              </>
            ) : (
              <>
                <Link to="/preferences/request">
                  <Button className="w-full bg-newsletter-primary hover:bg-newsletter-primary/90">
                    {t('preferences.loading.requestNewLink')}
                  </Button>
                </Link>
                <Link to="/">
                  <Button variant="ghost" className="w-full">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    {t('preferences.loading.backToHome')}
                  </Button>
                </Link>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <>
      {/* Welcome Success Modal */}
      <WelcomeSuccessModal 
        open={showWelcomeModal}
        onClose={() => setShowWelcomeModal(false)}
        userEmail={userData?.email || ''}
        onCustomize={handleWelcomeModalCustomize}
      />

      <div className="min-h-screen bg-background relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-newsletter-gradient opacity-5" />
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-newsletter-primary/10 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-newsletter-accent/10 rounded-full blur-3xl" />
      
      {/* Compact Header for Preferences */}
      <header className="relative z-20 w-full py-4 border-b border-border/20">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
              <img 
                src="/assets/logo.png" 
                alt="NewsForYou Logo"
                className="h-12 w-auto object-contain"
                style={{ filter: 'hue-rotate(12deg)' }}
              />
              <div className="hidden sm:block">
                <h1 className="text-lg font-bold text-newsletter-primary">NewsForYou</h1>
                <p className="text-xs text-muted-foreground">{t('preferences.header.tagline')}</p>
              </div>
            </Link>
            <div className="flex items-center gap-4">
              <span className="text-sm text-muted-foreground">
                {userData?.email}
              </span>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={handleLogout}
                className="gap-2 hover:bg-newsletter-primary/10 hover:text-newsletter-primary"
              >
                <LogOut className="w-4 h-4" />
                {t('preferences.header.logout')}
              </Button>
            </div>
          </div>
        </div>
      </header>
      
      <div className="relative z-10">
        <div className="container mx-auto px-4 py-12">
          {/* Trial/Subscription Status Card */}
          <div className="mb-8">
            <TrialStatusCard 
              sessionToken={sessionData?.session_token || ''} 
              userEmail={userData?.email || ''} 
              onStatusUpdate={setSubscriptionStatus}
            />
          </div>
          
          {/* User Header */}
          <div className="mb-8">
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Settings className="w-6 h-6" />
              {t('preferences.header.pageTitle')}
            </h1>
            <p className="text-muted-foreground mt-1">
              {t('preferences.header.pageSubtitle')} • {userData?.email}
            </p>
          </div>

          {/* Premium Settings Card */}
          {(() => {
            // Read session_token from localStorage first (source of truth)
            const storedSession = localStorage.getItem('newsletter_session');
            const token = storedSession ? JSON.parse(storedSession).session_token : sessionData?.session_token;

            if (!token) {
              return (
                <div className="mb-8">
                  <Card>
                    <CardContent className="flex items-center justify-center py-12">
                      <Loader2 className="w-6 h-6 animate-spin text-newsletter-primary" />
                      <span className="ml-3 text-muted-foreground">
                        {t('preferences.loading.validatingAccess')}
                      </span>
                    </CardContent>
                  </Card>
                </div>
              );
            }

            return (
              <div className="mb-8">
                <PremiumSettingsCard
                  userData={userData}
                  sessionToken={token}
                  subscriptionStatus={subscriptionStatus}
                  isLoadingSubscription={isLoadingSubscription}
                  initialSettings={premiumSettings}
                  interests={interests}
                  onInterestsChange={setInterests}
                  onSettingsUpdate={(newSettings) => {
                    setPremiumSettings(newSettings);
                  }}
                />
              </div>
            );
          })()}

          {/* Email Notifications Management Card */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {emailNotificationsEnabled ? (
                  <Mail className="w-5 h-5 text-newsletter-primary" />
                ) : (
                  <MailX className="w-5 h-5 text-muted-foreground" />
                )}
                {t('preferences.notifications.title')}
              </CardTitle>
              <CardDescription>
                {t('preferences.notifications.description')}
              </CardDescription>
            </CardHeader>
            
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 border border-border rounded-lg">
                <div className="space-y-1">
                  <div className="font-medium">
                    {t('preferences.notifications.statusTitle')}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {emailNotificationsEnabled 
                      ? t('preferences.notifications.enabled')
                      : t('preferences.notifications.disabled')
                    }
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-muted-foreground">
                    {emailNotificationsEnabled ? t('preferences.notifications.active') : t('preferences.notifications.paused')}
                  </span>
                  <Switch 
                    checked={emailNotificationsEnabled}
                    onCheckedChange={handleToggleEmailNotifications}
                    disabled={isTogglingNotifications}
                  />
                </div>
              </div>
              
              {!emailNotificationsEnabled && (
                <div className="p-3 bg-orange-500/10 border border-orange-500/20 rounded-lg">
                  <p className="text-sm text-orange-600 dark:text-orange-400">
                    {t('preferences.notifications.warning')}
                  </p>
                </div>
              )}
              
              <div className="pt-2">
                <Link to={`/unsubscribe/${userData?.id}`}>
                  <Button variant="outline" size="sm" className="gap-2">
                    <Settings className="w-4 h-4" />
                    {t('preferences.notifications.advancedOptions')}
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>

          {/* Subscription Management Card */}
          <Card>
            <CardHeader>
              <CardTitle>{t('preferences.subscription.title')}</CardTitle>
              <CardDescription>
                {t('preferences.subscription.description')}
              </CardDescription>
            </CardHeader>
            
            <CardContent className="space-y-4">
              {/* Main Subscription Button */}
              <Link to="/checkout">
                <Button 
                  variant="outline"
                  className="w-full h-auto p-4 border-newsletter-primary/20 hover:border-newsletter-primary hover:bg-newsletter-primary/10"
                >
                  <div className="flex items-start gap-3 w-full">
                    <Settings className="w-5 h-5 text-newsletter-primary flex-shrink-0" />
                    <div className="text-left flex-1 min-w-0">
                      <div className="font-semibold text-base">{t('preferences.subscription.managePlans')}</div>
                      <div className="text-sm text-muted-foreground break-words whitespace-normal">{t('preferences.subscription.managePlansDesc')}</div>
                    </div>
                  </div>
                </Button>
              </Link>

              {/* Customer Portal Button */}
              <Button 
                variant="ghost"
                onClick={handleManageSubscription}
                disabled={isPortalLoading}
                className="w-full h-auto p-4 hover:bg-newsletter-primary/10 hover:text-newsletter-primary"
              >
                {isPortalLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t('preferences.loading.openingPortal')}
                  </>
                ) : (
                  <div className="flex items-start gap-3 w-full">
                    <CreditCard className="w-5 h-5 flex-shrink-0" />
                    <div className="text-left flex-1 min-w-0">
                      <div className="font-semibold text-base">{t('preferences.subscription.managePayments')}</div>
                      <div className="text-sm text-muted-foreground break-words whitespace-normal">{t('preferences.subscription.managePaymentsDesc')}</div>
                    </div>
                  </div>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
    </>
  );
};

export default Preferences;